import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { Loader2Icon } from "lucide-react";

import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "js/components/ui/form";
import { Input } from "js/components/ui/input";
import { DatePicker } from "js/components/ui/date-picker";
import { CountryCodeSelect } from "js/pages/Tenants/components/CountryCodeSelect";

import { sanitizeEmailInput } from "../../../../utils/inputFilters";
import {
  validateEmail,
  validateEmailNotAdvisor,
  validatePhoneNotAdvisor,
  validatePhoneNumber,
} from "../../../../utils/validators/generalValidators";
import { validateName } from "../../../../utils/validators/tenantFormValidators";
import { GuarantorDetails, LandlordDetails, TenantDetails } from "./typing";

// Define a union type for all person types
type PersonType = TenantDetails | GuarantorDetails | LandlordDetails;

// Define the props interface for the component
interface PersonEditModalProps {
  isVisible: boolean;
  onCancel: () => void;
  onOk: (values: any) => void;
  personData: PersonType;
  personType: "tenant" | "guarantor" | "landlord";
  loading?: boolean;
  title?: string;
  submitLabel?: string;
}

interface PersonFormValues {
  name: string;
  first_surname: string;
  second_surname: string;
  email: string;
  phone_number: string;
  dial_code: string;
  rfc: string;
  curp: string;
  birth_date: Date | null;
  legal_type: any;
}

const getModalTitle = (personType: string): string => {
  switch (personType) {
    case "tenant":
      return "Editar Detalles del Inquilino";
    case "guarantor":
      return "Editar Detalles del Fiador/Obligado Solidario";
    case "landlord":
      return "Editar Detalles del Propietario";
    default:
      return "Editar Detalles";
  }
};

const PersonEditModal: React.FC<PersonEditModalProps> = ({
  isVisible,
  onCancel,
  onOk,
  personData,
  personType,
  loading = false,
  title,
  submitLabel,
}) => {
  const { userData } = useSelector((state: any) => state.entities.user);

  const form = useForm<PersonFormValues>({
    defaultValues: {
      name: "",
      first_surname: "",
      second_surname: "",
      email: "",
      phone_number: "",
      dial_code: "+52",
      rfc: "",
      curp: "",
      birth_date: null,
      legal_type: null,
    },
  });

  // Set form values when personData changes
  useEffect(() => {
    if (isVisible && personData) {
      form.reset({
        name: personData.name || "",
        first_surname: personData.first_surname || "",
        second_surname: personData.second_surname || "",
        email: personData.email || "",
        phone_number: String(personData.phone_number || ""),
        dial_code: (personData as any).dial_code || "+52",
        rfc: personData.rfc || "",
        curp: personData.curp || "",
        birth_date: personData.birth_date
          ? dayjs(personData.birth_date, "DD/MM/YYYY").toDate()
          : null,
        legal_type: personData.legal_type,
      });
    }
  }, [form, personData, isVisible]);

  const handleSubmit = (values: PersonFormValues) => {
    const formattedValues = {
      ...values,
      birth_date: values.birth_date
        ? dayjs(values.birth_date).format("DD/MM/YYYY")
        : null,
    };
    onOk(formattedValues);
  };

  // Wrap async validators from Ant Design format to react-hook-form format
  const wrapValidator = (
    fn: (...args: any[]) => Promise<void>,
    ...extraArgs: any[]
  ) => {
    return async (value: string) => {
      try {
        await fn({}, value, ...extraArgs);
        return true;
      } catch (error: any) {
        return error?.message || error || "Valor inválido";
      }
    };
  };

  return (
    <Dialog open={isVisible} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{title ?? getModalTitle(personType)}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="name"
                rules={{
                  required: "Este campo es requerido",
                  validate: wrapValidator(validateName),
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input placeholder="Nombre" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="first_surname"
                rules={{
                  required: "Este campo es requerido",
                  validate: wrapValidator(validateName),
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primer Apellido *</FormLabel>
                    <FormControl>
                      <Input placeholder="Primer Apellido" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="second_surname"
                rules={{
                  validate: wrapValidator(validateName),
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Segundo Apellido</FormLabel>
                    <FormControl>
                      <Input placeholder="Segundo Apellido" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                rules={{
                  required: "El correo electrónico es requerido",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Por favor ingrese un correo electrónico válido",
                  },
                  validate: {
                    validEmail: wrapValidator(validateEmail),
                    notAdvisor: async (value) => {
                      try {
                        await validateEmailNotAdvisor(userData, value);
                        return true;
                      } catch (error: any) {
                        return error?.message || "Correo inválido";
                      }
                    },
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Correo *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Correo"
                        maxLength={100}
                        {...field}
                        onChange={(e) =>
                          field.onChange(sanitizeEmailInput(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone_number"
                rules={{
                  required: "Este campo es requerido",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "El teléfono debe tener 10 dígitos numéricos",
                  },
                  validate: {
                    validPhone: wrapValidator(validatePhoneNumber),
                    notAdvisor: async (value) => {
                      try {
                        await validatePhoneNotAdvisor(userData, value);
                        return true;
                      } catch (error: any) {
                        return error?.message || "Teléfono inválido";
                      }
                    },
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teléfono *</FormLabel>
                    <div className="flex gap-2">
                      <div className="w-32 [&_button]:h-9">
                        <CountryCodeSelect
                          value={form.watch("dial_code")}
                          onValueChange={(value) =>
                            form.setValue("dial_code", value)
                          }
                        />
                      </div>
                      <FormControl>
                        <Input
                          placeholder="Teléfono"
                          maxLength={10}
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {personType !== "guarantor" && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="rfc"
                  rules={{
                    minLength: {
                      value: 10,
                      message: "El RFC debe tener minimo 10 caracteres",
                    },
                    maxLength: {
                      value: 13,
                      message: "El RFC debe tener máximo 13 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>RFC</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="RFC"
                          maxLength={13}
                          className="uppercase"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="curp"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CURP</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="CURP"
                          maxLength={18}
                          className="uppercase"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="birth_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Nacimiento</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value ?? undefined}
                          onChange={field.onChange}
                          placeholder="DD/MM/AAAA"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2Icon className="size-4 animate-spin" />}
                {submitLabel ?? "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
export default PersonEditModal;
